import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../services/api';
import Icon from '../components/Icon';

const GAME_META = {
  lightspeed: { label: 'LightSpeed', path: '/lightspeed', icon: 'LS' },
  cirquiz:    { label: 'CirQuiz',    path: '/quiz',       icon: 'CQ' },
  podiumquiz: { label: 'PodiumQuiz', path: '/podium',     icon: 'PQ' },
};

const FriendContext = createContext(null);

export const FriendProvider = ({ children }) => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [modalRequest, setModalRequest] = useState(null);
  const [friends, setFriends] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [pendingChallenges, setPendingChallenges] = useState([]);
  const [modalChallenge, setModalChallenge] = useState(null);
  const intervalRef = useRef(null);
  const shownChallengeIds = useRef(new Set());

  const fetchRequests = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/friends/requests');
      setRequests(res.data);
      if (res.data.length > 0 && !modalRequest) setModalRequest(res.data[0]);
    } catch {}
  }, [user, modalRequest]);

  const fetchFriends = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/friends');
      setFriends(res.data);
    } catch {}
  }, [user]);

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/messages/unread-count');
      setUnreadTotal(res.data.total || 0);
    } catch {}
  }, [user]);

  const fetchPendingChallenges = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/challenges');
      const incoming = res.data.filter(c =>
        c.status === 'pending' && String(c.challenged?._id) === String(user.id)
      );
      setPendingChallenges(incoming);
      // Show first unseen pending challenge
      const unseen = incoming.find(c => !shownChallengeIds.current.has(c._id));
      if (unseen && !modalChallenge) {
        shownChallengeIds.current.add(unseen._id);
        setModalChallenge(unseen);
      }
    } catch {}
  }, [user, modalChallenge]);

  const pollAll = useCallback(() => {
    fetchRequests();
    fetchUnread();
    fetchPendingChallenges();
  }, [fetchRequests, fetchUnread, fetchPendingChallenges]);

  useEffect(() => {
    if (!user) {
      setRequests([]); setFriends([]); setModalRequest(null);
      setUnreadTotal(0); setPendingChallenges([]); setModalChallenge(null);
      return;
    }
    fetchFriends();
    pollAll();
    intervalRef.current = setInterval(pollAll, 30000);
    return () => clearInterval(intervalRef.current);
  }, [user]);

  const accept = async (senderId) => {
    await api.post(`/friends/accept/${senderId}`);
    setRequests(r => r.filter(u => u._id !== senderId));
    setModalRequest(null);
    fetchFriends();
  };

  const decline = async (senderId) => {
    await api.delete(`/friends/decline/${senderId}`);
    setRequests(r => r.filter(u => u._id !== senderId));
    setModalRequest(null);
  };

  const sendRequest = async (userId) => {
    await api.post(`/friends/request/${userId}`);
  };

  const removeFriend = async (userId) => {
    await api.delete(`/friends/decline/${userId}`);
    fetchFriends();
  };

  const isFriend = (userId) => friends.some(f => f._id === userId);
  const hasPendingFrom = (userId) => requests.some(r => r._id === userId);

  const acceptChallenge = async (id) => {
    await api.patch(`/challenges/${id}/accept`);
    setPendingChallenges(p => p.filter(c => c._id !== id));
    setModalChallenge(null);
  };

  const declineChallenge = async (id) => {
    await api.patch(`/challenges/${id}/decline`);
    setPendingChallenges(p => p.filter(c => c._id !== id));
    setModalChallenge(null);
  };

  return (
    <FriendContext.Provider value={{ requests, friends, modalRequest, setModalRequest, accept, decline, sendRequest, removeFriend, isFriend, hasPendingFrom, fetchRequests, fetchFriends, unreadTotal, fetchUnread, pendingChallenges, acceptChallenge, declineChallenge }}>
      {children}
      {modalRequest && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-white font-black text-lg mb-1">Invitation d'amitié</h3>
            <p className="text-zinc-400 text-sm mb-5">
              <span className="text-white font-semibold">{modalRequest.username}</span> souhaite t'ajouter en ami.
            </p>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-zinc-700">
                {modalRequest.avatar
                  ? <img src={modalRequest.avatar} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-f1red/20 flex items-center justify-center text-xl font-black text-f1red">
                      {modalRequest.username?.[0]?.toUpperCase()}
                    </div>
                }
              </div>
              <div>
                <p className="text-white font-bold">{modalRequest.username}</p>
                <p className="text-zinc-500 text-xs">Niveau {modalRequest.level ?? 0}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => accept(modalRequest._id)}
                className="flex-1 btn-primary py-2.5 font-bold">Accepter</button>
              <button onClick={() => decline(modalRequest._id)}
                className="flex-1 btn-secondary py-2.5 font-bold">Refuser</button>
            </div>
          </div>
        </div>
      )}
      {modalChallenge && (() => {
        const meta = GAME_META[modalChallenge.game];
        const isGameChallenge = !!meta;
        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-sm shadow-2xl"
              style={{ borderColor:'rgba(225,6,0,.4)', boxShadow:'0 0 40px rgba(225,6,0,.15)' }}>
              <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-1 flex items-center gap-1"><Icon name="sword" size={9} /> Défi reçu !</p>
              <h3 className="text-white font-black text-xl mb-1">
                {isGameChallenge ? meta.icon : <Icon name="flag" size={16} className="inline" />}{' '}
                {isGameChallenge ? meta.label : modalChallenge.race?.raceName}
              </h3>
              <p className="text-zinc-400 text-sm mb-5">
                <span className="text-white font-semibold">{modalChallenge.challenger?.username}</span>
                {isGameChallenge ? ' te lance un défi sur ' : ' te défie sur '}
                {isGameChallenge
                  ? <span className="text-f1red font-bold">{meta.label}</span>
                  : <span className="text-f1red font-bold">{modalChallenge.race?.raceName}</span>
                }
              </p>
              <div className="flex gap-3">
                {isGameChallenge ? (
                  <Link to={meta.path}
                    onClick={() => { acceptChallenge(modalChallenge._id); }}
                    className="flex-1 btn-primary py-2.5 font-bold text-center">
                    Accepter &amp; Jouer
                  </Link>
                ) : (
                  <button onClick={() => acceptChallenge(modalChallenge._id)}
                    className="flex-1 btn-primary py-2.5 font-bold">
                    Accepter
                  </button>
                )}
                <button onClick={() => declineChallenge(modalChallenge._id)}
                  className="flex-1 btn-secondary py-2.5 font-bold">Refuser</button>
              </div>
            </div>
          </div>
        );
      })()}
    </FriendContext.Provider>
  );
};

export const useFriends = () => useContext(FriendContext);
